import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { companyApi, errorMessage, qk } from '@/api';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Textarea } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { useJob } from '@/features/jobs/useJobs';

import { useEmailReport } from './useEntries';

const splitEmails = (s: string) =>
  s
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);

/** W4 "Email report": prefilled from the job's client emails, falling back to the company default list (doc 17). */
export function EmailReportDialog({
  open,
  onClose,
  entryId,
  jobId,
  label,
}: {
  open: boolean;
  onClose: () => void;
  entryId: string;
  jobId: string;
  label: string;
}) {
  const job = useJob(jobId);
  const company = useQuery({
    queryKey: qk.company(),
    queryFn: companyApi.getCompany,
    staleTime: 5 * 60_000,
  });
  const send = useEmailReport(entryId);
  const toast = useToast();
  // null = untouched, so the prefill can arrive after the dialog opens.
  const [edited, setEdited] = useState<string | null>(null);
  const prefill = (
    job.data?.clientEmails.length
      ? job.data.clientEmails
      : (company.data?.clientDefaultEmails ?? [])
  ).join(', ');
  const to = edited ?? prefill;
  const emails = splitEmails(to);
  const invalid = emails.filter((e) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Email daily report"
      description={`${label} — the PDF goes as an attachment.`}
      width="sm"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={emails.length === 0 || invalid.length > 0}
            loading={send.isPending}
            onClick={() =>
              send.mutate(emails, {
                onSuccess: (r) => {
                  toast.success(
                    `Report queued for ${r.sentTo.length} recipient${r.sentTo.length === 1 ? '' : 's'}`,
                  );
                  onClose();
                },
                onError: (e) => toast.error(errorMessage(e)),
              })
            }
          >
            Send report
          </Button>
        </>
      }
    >
      <Field
        label="To"
        htmlFor="email-to"
        hint="Comma separated. Prefilled from the job's client emails."
        error={invalid.length ? `Check: ${invalid.join(', ')}` : undefined}
      >
        <Textarea
          id="email-to"
          autoFocus
          value={to}
          onChange={(e) => setEdited(e.target.value)}
          rows={3}
        />
      </Field>
    </Dialog>
  );
}
